/**
 * Identity state machine composable.
 *
 * Drives UI/UX decisions (show free-bet button vs sign-up-to-claim vs
 * login-only) from two localStorage flags, eliminating per-page-load
 * Fingerprint Pro API calls.
 *
 * State persisted under `localStorage["aurum_identity"]` as JSON:
 *   {
 *     user_registered: boolean,
 *     visitor_id: string | null
 *   }
 *
 * Transitions (Fingerprint Pro calls are MINIMIZED):
 *   FRESH  (no flags)                → eligible for free bet
 *   PLAYED (visitor_id, no user)     → "Sign up to claim $X" UX
 *   REGISTERED (user_registered=true)→ no free bet, normal auth UI
 *
 * On ban: state goes REGISTERED — `user_registered=true` locks the UI
 * permanently for this browser, ban screen shown via auth response.
 *
 * Spec: docs/superpowers/specs/2026-05-26-identity-state-machine-design.md
 */

const STORAGE_KEY = "aurum_identity"

export interface IdentityState {
  user_registered: boolean
  visitor_id: string | null
  /**
   * Sticky flag: backend told us (via /visitor-eligibility canShowFreeBet=false
   * OR /visitor-free-round 400 with one of the identity-risk error codes) that
   * this device/IP is not eligible for a free bet. Set after the user
   * acknowledges the explanation modal. Once set, the UI permanently shows a
   * plain "Sign Up" CTA instead of any free-bet promise — even if a future
   * eligibility check flips back to true (e.g. admin cleared the block).
   */
  ineligible_for_free_bet: boolean
}

const EMPTY_STATE: IdentityState = {
  user_registered: false,
  visitor_id: null,
  ineligible_for_free_bet: false,
}

function readStorage(): IdentityState {
  if (typeof localStorage === "undefined") return { ...EMPTY_STATE }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...EMPTY_STATE }
    const parsed = JSON.parse(raw)
    return {
      user_registered: parsed.user_registered === true,
      visitor_id: typeof parsed.visitor_id === "string" ? parsed.visitor_id : null,
      ineligible_for_free_bet: parsed.ineligible_for_free_bet === true,
    }
  } catch {
    return { ...EMPTY_STATE }
  }
}

function writeStorage(state: IdentityState): void {
  if (typeof localStorage === "undefined") return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (err) {
    console.warn("[useIdentity] Failed to persist identity state:", err)
  }
}

export function useIdentity() {
  // Module-level shared state — single source of truth across components.
  const state = useState<IdentityState>("identity", () => readStorage())

  // Cross-tab sync via storage event. Updates state when another tab
  // changes the entry (e.g. login in tab A → reflect in tab B).
  if (import.meta.client && typeof window !== "undefined") {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        state.value = readStorage()
      }
    }
    // Idempotent: addEventListener with same handler reference dedupes.
    window.addEventListener("storage", handler)
  }

  function markRegistered(visitorId?: string | null): void {
    state.value = {
      ...state.value,
      user_registered: true,
      // On login, visitor_id is no longer needed in localStorage — the
      // visitor is now bound to the user in the DB. Keep it null to
      // prevent stale state. On signup, the visitor was either supplied
      // by the client (in which case it's known to the backend) or
      // created by the backend during signup; either way, we don't need
      // it on the client anymore.
      visitor_id: visitorId ?? null,
    }
    writeStorage(state.value)
  }

  function markPlayed(visitorId: string): void {
    state.value = {
      ...state.value,
      user_registered: false,
      visitor_id: visitorId,
    }
    writeStorage(state.value)
  }

  function markBanned(): void {
    // Same as registered — UI is locked permanently for this browser.
    // The "banned" distinction is conveyed by the auth response, not
    // the identity state.
    state.value = {
      ...state.value,
      user_registered: true,
      visitor_id: null,
    }
    writeStorage(state.value)
  }

  /**
   * Sticky-mark this browser as ineligible for any free bet (page-load
   * eligibility said no, OR the click submit was rejected by an identity-risk
   * gate). Called after the user acknowledges the explanation modal. After
   * this, the bet panel permanently shows "Sign Up to play" without any
   * free-bet CTA, even on hard reload.
   */
  function markIneligibleForFreeBet(): void {
    state.value = {
      ...state.value,
      ineligible_for_free_bet: true,
    }
    writeStorage(state.value)
  }

  function reset(): void {
    state.value = { ...EMPTY_STATE }
    if (typeof localStorage !== "undefined") {
      try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    }
  }

  /**
   * "Is this browser eligible to play / show a free-bet CTA?"
   * Cheap synchronous check that does NOT call Fingerprint Pro.
   * Backend /visitor-eligibility provides the authoritative answer
   * after checking IP and visitor history.
   */
  const isFresh = computed(() => !state.value.user_registered && !state.value.visitor_id)
  const isPlayed = computed(() => !state.value.user_registered && !!state.value.visitor_id)
  const isRegistered = computed(() => state.value.user_registered)
  const isIneligibleForFreeBet = computed(() => state.value.ineligible_for_free_bet)

  return {
    state: readonly(state),
    isFresh,
    isPlayed,
    isRegistered,
    isIneligibleForFreeBet,
    markRegistered,
    markPlayed,
    markBanned,
    markIneligibleForFreeBet,
    reset,
  }
}
